/**
 * GraphQL documents for Phase 2 split search queries + overview stream.
 *
 * Three split queries (content, playlists, profiles) fire in parallel.
 * OVERVIEW_STREAM_SUBSCRIPTION subscribes to AI Service's oracleSearchOverviewStream
 * for the Sage narrative. Requires chatSessionId + intent from the aiSearch response.
 */

import { gql } from '@apollo/client'
import { PLAYLIST_ITEM_CONTENT_FIELDS } from '../fragments'

// ── Split queries (parallel from frontend) ─────────────────────────

export const AI_SEARCH_CONTENT_QUERY = gql`
  query AiSearchContent(
    $query: String
    $contentLimit: Int
    $contentOffset: Int
    $sessionId: ID
    $filters: AiSearchFilters
  ) {
    aiSearchContent(
      query: $query
      contentLimit: $contentLimit
      contentOffset: $contentOffset
      sessionId: $sessionId
      filters: $filters
    ) {
      content {
        id
        contentId
        contentType
        title
        score
        channel
        personalizationBoost
        path
        thumbnailUrl
        heroImageUrl
        instructor
        durationSeconds
        mediaId
        displayType
        studio
        centers
        level
        seriesTitle
        seriesContentId
        episodeNumber
        seasonNumber
        topics
        contentReferences { type name }
      }
      contentTotalCount
      topContentIds
      topContentScores
      entities {
        contentId
        contentType
        entityType
        name
        titleGroupId
        confidence
        matchType
      }
      intentSignal {
        classification
        reason
        requiresLlm
      }
      adjacency {
        centerId
        centerName
        sharedContentCount
        adjacencyType
      }
      chatSessionId
    }
  }
`

export const AI_SEARCH_PLAYLISTS_QUERY = gql`
  ${PLAYLIST_ITEM_CONTENT_FIELDS}
  query AiSearchPlaylists(
    $query: String!
    $topContentIds: [Int!]!
    $topContentScores: [Float!]!
    $playlistLimit: Int
    $language: [String]
  ) {
    aiSearchPlaylists(
      query: $query
      topContentIds: $topContentIds
      topContentScores: $topContentScores
      playlistLimit: $playlistLimit
    ) {
      playlists {
        playlistId
        playlistName
        description
        itemCount
        matchedContentCount
        textRelevance
        score
        previewThumbnailUrls
        previewContentTitles
        creatorDisplayName
        playlist {
          id
          visibility
          followerCount
          isFollowing
          userProfile {
            uuid
            displayName
            avatar {
              url
              small
              card
            }
          }
          playlistItems {
            ...PlaylistItemContentFields
          }
        }
      }
      playlistTotalCount
    }
  }
`

export const AI_SEARCH_PROFILES_QUERY = gql`
  query AiSearchProfiles(
    $query: String!
    $profileLimit: Int
  ) {
    aiSearchProfiles(
      query: $query
      profileLimit: $profileLimit
    ) {
      uid
      bio
      location
      score
      userProfile {
        uid
        uuid
        displayName
        bio
        followerCount
        profileVisibility
        avatar {
          url
          small
          card
        }
        viewerContext {
          isFollowing
          isFollowedBy
        }
        topics {
          contentId
          title
        }
        topGenres(limit: 5) {
          name
          cleanName
        }
      }
    }
  }
`

// ── Overview stream subscription ────────────────────────────────────

export const OVERVIEW_STREAM_SUBSCRIPTION = gql`
  subscription SearchOverview($input: SearchOverviewStreamInput!) {
    oracleSearchOverviewStream(input: $input) {
      chatSessionId
      turnId
      textChunk
      statusText
      refinementSuggestions
      executionTime
      isFinal
      success
      errors
    }
  }
`
